// ═══════════════════════════════════════════════════════
// Schoolizy — Espace Parent
// Vue détaillée d'un enfant : notes, absences, paiements
// ═══════════════════════════════════════════════════════
import { useEffect, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { api } from '../api/client';
import { useParent } from '../context/ParentContext'; // safe — ne throw plus
import { formatXof } from '../utils/format';
import { APPRECIATION_NOTE } from '../utils/cycle.helpers';

export default function EspaceParent() {
  const { studentId } = useParams();
  const [searchParams] = useSearchParams();
  const section = searchParams.get('section') || 'notes';
  const navigate = useNavigate();
  const { studentIds } = useParent();

  const [student, setStudent] = useState(null);
  const [periods, setPeriods] = useState([]);
  const [periodId, setPeriodId] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    // Sécurité : vérifier que cet enfant appartient au parent
    if (studentIds.length > 0 && !studentIds.includes(studentId)) {
      setError("Vous n'avez pas accès à cet élève.");
      return;
    }
    api.get(`/students/${studentId}`).then(setStudent).catch((e) => setError(e.message));
    api.get('/catalog/periods').then((p) => { setPeriods(p || []); if (p?.[0]) setPeriodId(p[0].id); }).catch(() => {});
  }, [studentId, studentIds]);

  if (error) return <div className="form-error">{error}</div>;
  if (!student) return <div className="loading">Chargement…</div>;

  const className = student.enrollments?.[0]?.class?.name || '—';
  const classId   = student.enrollments?.[0]?.classId;

  return (
    <>
      {/* En-tête élève */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 20 }}>
        <button className="btn-ghost btn-sm" onClick={() => navigate('/dashboard')}>← Retour</button>
        <div style={{ width: 48, height: 48, borderRadius: '50%', background: 'var(--primary)', color: '#fff', display: 'grid', placeItems: 'center', fontWeight: 700, fontSize: '1.1rem' }}>
          {student.firstName?.[0]}{student.lastName?.[0]}
        </div>
        <div>
          <h1 style={{ fontSize: '1.1rem', fontWeight: 700 }}>{student.lastName} {student.firstName}</h1>
          <div style={{ fontSize: 12, color: 'var(--muted)' }}>{className} · {student.matricule}</div>
        </div>
      </div>

      {/* Tabs */}
      <div className="tabs">
        {[['notes', '📝 Notes'], ['presences', '✅ Présences'], ['paiements', '💰 Paiements'], ['bulletin', '📋 Bulletin']].map(([s, l]) => (
          <button key={s} className={`tab ${section === s ? 'active' : ''}`}
            onClick={() => navigate(`/parent/enfant/${studentId}?section=${s}`)}>
            {l}
          </button>
        ))}
      </div>

      {section === 'notes'     && <NotesParent studentId={studentId} classId={classId} periodId={periodId} periods={periods} onPeriodChange={setPeriodId} />}
      {section === 'presences' && <PresencesParent studentId={studentId} />}
      {section === 'paiements' && <PaiementsParent studentId={studentId} />}
      {section === 'bulletin'  && <BulletinParent studentId={studentId} classId={classId} periodId={periodId} periods={periods} onPeriodChange={setPeriodId} />}
    </>
  );
}

// ── Notes ─────────────────────────────────────────────
function NotesParent({ studentId, classId, periodId, periods, onPeriodChange }) {
  const [evals, setEvals] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!classId) return;
    setLoading(true);
    api.get(`/evaluations?classId=${classId}${periodId ? `&periodId=${periodId}` : ''}`).then((data) => {
      setEvals((data || []).filter((ev) => (ev.results || []).some((r) => r.studentId === studentId)));
    }).catch(() => {}).finally(() => setLoading(false));
  }, [classId, periodId]);

  return (
    <div className="card">
      <div className="card-head">
        <h2>Notes & évaluations</h2>
        <select value={periodId} onChange={(e) => onPeriodChange(e.target.value)} style={{ width: 150 }}>
          {periods.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
        </select>
      </div>
      <div className="card-body">
        {loading ? <div className="loading">Chargement…</div> : evals.length === 0 ? (
          <div className="empty">Aucune note disponible pour ce trimestre.</div>
        ) : (
          <div className="table-wrap"><table>
            <thead><tr><th>Date</th><th>Évaluation</th><th>Matière</th><th>Type</th><th className="num">Note</th><th className="num">Barème</th><th>Appréciation</th></tr></thead>
            <tbody>
              {evals.map((ev) => {
                const result = (ev.results || []).find((r) => r.studentId === studentId);
                const note   = result?.totalObtained;
                const absent = result?.absent;
                const sur20  = note != null ? (note / ev.baremeTotal * 20).toFixed(2) : null;
                const appr   = sur20 ? APPRECIATION_NOTE(Number(sur20), 20) : '';
                const color  = sur20 ? (Number(sur20) >= 10 ? 'var(--success)' : 'var(--danger)') : 'var(--muted)';
                return (
                  <tr key={ev.id}>
                    <td className="mono">{ev.date ? new Date(ev.date).toLocaleDateString('fr-FR') : '—'}</td>
                    <td><b>{ev.title}</b></td>
                    <td>{ev.subject?.name || '—'}</td>
                    <td><span className="badge gray" style={{ fontSize: 10 }}>{ev.type}</span></td>
                    <td className="num mono" style={{ fontWeight: 700, color: absent ? 'var(--warn)' : color }}>
                      {absent ? 'Abs' : note != null ? note : '—'}
                    </td>
                    <td className="num">/{ev.baremeTotal}</td>
                    <td style={{ fontSize: 12, color }}>{absent ? 'Absent' : appr}</td>
                  </tr>
                );
              })}
            </tbody>
          </table></div>
        )}
      </div>
    </div>
  );
}

// ── Présences ─────────────────────────────────────────
function PresencesParent({ studentId }) {
  const [absences, setAbsences] = useState([]);
  const [stats, setStats]       = useState({ absences: 0, retards: 0, excuses: 0 });

  useEffect(() => {
    api.get('/registry/presences-eleves').then((d) => {
      const all = (Array.isArray(d) ? d : (d?.records || [])).map((r) => ({ id: r.id, ...r.data }));
      const entries = all.flatMap((r) =>
        (r.entries || []).filter((e) => e.studentId === studentId).map((e) => ({ ...e, date: r.date }))
      );
      setAbsences(entries.filter((e) => e.status !== 'PRESENT').sort((a, b) => (b.date || '').localeCompare(a.date || '')));
      setStats({
        absences: entries.filter((e) => e.status === 'ABSENT').length,
        retards:  entries.filter((e) => e.status === 'RETARD').length,
        excuses:  entries.filter((e) => e.status === 'EXCUSE').length,
      });
    }).catch(() => {});
  }, [studentId]);

  return (
    <>
      <div className="kpi-grid" style={{ marginBottom: 16 }}>
        <div className="kpi"><span>Absences</span><strong style={{ color: stats.absences > 0 ? 'var(--danger)' : 'inherit' }}>{stats.absences}</strong></div>
        <div className="kpi gold"><span>Retards</span><strong>{stats.retards}</strong></div>
        <div className="kpi navy"><span>Excusés</span><strong>{stats.excuses}</strong></div>
      </div>
      <div className="card">
        <div className="card-head"><h2>Historique des absences</h2></div>
        <div className="card-body">
          {absences.length === 0 ? <div className="empty">✅ Aucune absence enregistrée.</div> : (
            <div className="table-wrap"><table>
              <thead><tr><th>Date</th><th>Statut</th><th>Motif</th></tr></thead>
              <tbody>{absences.map((a, i) => (
                <tr key={i}>
                  <td className="mono">{a.date ? new Date(a.date + 'T12:00:00').toLocaleDateString('fr-FR', { weekday: 'long', day: '2-digit', month: 'long', year: 'numeric' }) : '—'}</td>
                  <td><span className={`badge ${a.status === 'ABSENT' ? 'red' : a.status === 'RETARD' ? 'gold' : 'blue'}`}>{a.status}</span></td>
                  <td>{a.note || '—'}</td>
                </tr>
              ))}</tbody>
            </table></div>
          )}
        </div>
      </div>
    </>
  );
}

// ── Paiements ─────────────────────────────────────────
function PaiementsParent({ studentId }) {
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading]   = useState(true);

  useEffect(() => {
    api.get(`/finance/students/${studentId}/situation`).then((data) => {
      setInvoices(data?.invoices || []);
    }).catch(() => {}).finally(() => setLoading(false));
  }, [studentId]);

  const totalDu   = invoices.reduce((s, i) => s + (i.amountXof || 0), 0);
  const totalPaye = invoices.reduce((s, i) => s + (i.paid || 0), 0);
  const solde     = totalDu - totalPaye;

  return (
    <>
      <div className="kpi-grid" style={{ marginBottom: 16 }}>
        <div className="kpi"><span>Total dû</span><strong className="mono">{formatXof(totalDu)}</strong></div>
        <div className="kpi green"><span>Total payé</span><strong className="mono">{formatXof(totalPaye)}</strong></div>
        <div className={`kpi ${solde > 0 ? 'red' : 'green'}`}><span>Solde restant</span><strong className="mono" style={{ color: solde > 0 ? 'var(--danger)' : 'var(--success)' }}>{formatXof(solde)}</strong></div>
      </div>
      <div className="card">
        <div className="card-head"><h2>Mes factures</h2></div>
        <div className="card-body">
          {loading ? <div className="loading">Chargement…</div> : invoices.length === 0 ? (
            <div className="empty">Aucune facture.</div>
          ) : (
            <div className="table-wrap"><table>
              <thead><tr><th>Libellé</th><th className="num">Montant</th><th className="num">Payé</th><th className="num">Reste</th><th>Statut</th></tr></thead>
              <tbody>{invoices.map((inv) => {
                const reste = (inv.amountXof || 0) - (inv.paid || 0);
                return (
                  <tr key={inv.id}>
                    <td>{inv.label}</td>
                    <td className="num mono">{formatXof(inv.amountXof || 0)}</td>
                    <td className="num mono" style={{ color: 'var(--success)' }}>{formatXof(inv.paid || 0)}</td>
                    <td className="num mono" style={{ color: reste > 0 ? 'var(--danger)' : 'var(--success)' }}>{formatXof(reste)}</td>
                    <td><span className={`badge ${inv.status === 'PAYEE' ? 'green' : inv.status === 'PARTIELLE' ? 'gold' : 'gray'}`}>{inv.status}</span></td>
                  </tr>
                );
              })}</tbody>
            </table></div>
          )}
        </div>
      </div>
    </>
  );
}

// ── Bulletin ──────────────────────────────────────────
function BulletinParent({ studentId, classId, periodId, periods, onPeriodChange }) {
  const navigate = useNavigate();
  return (
    <div className="card">
      <div className="card-head">
        <h2>Bulletin scolaire</h2>
        <select value={periodId} onChange={(e) => onPeriodChange(e.target.value)} style={{ width: 150 }}>
          {periods.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
        </select>
      </div>
      <div className="card-body" style={{ padding: 24, textAlign: 'center' }}>
        <div style={{ fontSize: '2.5rem', marginBottom: 12 }}>📋</div>
        <p style={{ color: 'var(--muted)', fontSize: 13, marginBottom: 20 }}>
          Consultez le bulletin scolaire complet de votre enfant.
        </p>
        <button className="btn" onClick={() => navigate(`/bulletins/${studentId}?periodId=${periodId}`)}
          style={{ width: 'auto', padding: '0 28px' }}>
          Voir le bulletin →
        </button>
      </div>
    </div>
  );
}
